import cn from 'classnames';
import { FC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { NoteLineOp } from '@/shared/api/protocol-ts/api/scribe/svc_encounter_pb';

import {
  changeNoteLine,
  type NoteOperation,
  type SurfaceSection,
} from '../lib';

import styles from './NoteSetAside.module.scss';
import { SettingsPopover } from './SettingsPopover';

type Props = {
  sections: SurfaceSection[];
  onOperation: (operation: NoteOperation) => void;
};

// The lines of the note removed or skipped, under the titles of their
// sections, each named by its text and brought back from the document
// toolbar.
export const NoteSetAside: FC<Props> = ({ sections, onOperation }) => {
  const { formatMessage } = useIntl();
  const groups = sections.filter((section) => section.setAside.length);
  const count = groups.reduce(
    (sum, section) => sum + section.setAside.length,
    0,
  );

  if (!count) {
    return null;
  }
  const label = formatMessage(
    {
      id: 'workspace.note.setAside',
      defaultMessage:
        '{count, plural, one {# line removed or skipped} other {# lines removed or skipped}}',
    },
    { count },
  );
  return (
    <SettingsPopover
      label={label}
      text={label}
      className="p4"
      dataComponent="workspace-note-set-aside-toggle"
    >
      <div className={styles.groups} data-component="workspace-note-set-aside">
        {groups.map((section) => (
          <section
            key={section.id}
            className={styles.group}
            aria-label={section.title}
          >
            <h4 className={cn('p3', styles.title)}>{section.title}</h4>
            <ul className={styles.list}>
              {section.setAside.map((line) => (
                <li key={line.id} className={cn('p3', styles.line)}>
                  <span className={cn(styles.text, styles.struck)}>
                    {line.text}
                  </span>
                  <span className={cn('p4', styles.caption)}>
                    {line.skipped ? (
                      <FormattedMessage
                        id="workspace.note.skipped"
                        defaultMessage="skipped"
                      />
                    ) : (
                      <FormattedMessage
                        id="workspace.note.removed"
                        defaultMessage="removed"
                      />
                    )}
                  </span>
                  <button
                    type="button"
                    className={cn('p4', styles.bringBack)}
                    onClick={() =>
                      onOperation(
                        changeNoteLine(
                          line.id,
                          line.skipped
                            ? NoteLineOp.NoteLineOpUnskip
                            : NoteLineOp.NoteLineOpRestore,
                        ),
                      )
                    }
                  >
                    <FormattedMessage
                      id="workspace.note.bringBack"
                      defaultMessage="Bring back"
                    />
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </SettingsPopover>
  );
};
